import type { ActionListItem } from '@shared/types/views';

/**
 * Filters and due-date groups of a long action list (layout lab,
 * interactions, 03/10/2026). Pure arrangement of the items received: the
 * order given inside a group is kept, nothing is stored.
 */

/** Items from which the list shows its filters and groups. */
export const ACTION_LIST_INTERACTIVE_FROM = 8;

export type ActionFilter = 'all' | 'open' | 'high' | 'closed';

const isClosed = (item: ActionListItem) => item.status === 'done' || item.status === 'skipped';

const MATCHES: Record<ActionFilter, (item: ActionListItem) => boolean> = {
  all: () => true,
  open: (item) => !isClosed(item),
  high: (item) => item.priority === 'high' && !isClosed(item),
  closed: isClosed,
};

const FILTER_LABELS: Record<ActionFilter, string> = {
  all: 'Toutes',
  open: 'À faire',
  high: 'Prioritaires',
  closed: 'Faites',
};

/** The filters worth offering: those that keep some items but not all of them. */
export const actionFilters = (items: ActionListItem[]): Array<{ id: ActionFilter; label: string; count: number }> => {
  if (items.length < ACTION_LIST_INTERACTIVE_FROM) return [];
  const useful = (['open', 'high', 'closed'] as const)
    .map((id) => ({ id, label: FILTER_LABELS[id], count: items.filter(MATCHES[id]).length }))
    .filter((filter) => filter.count > 0 && filter.count < items.length);
  return useful.length > 0 ? [{ id: 'all', label: FILTER_LABELS.all, count: items.length }, ...useful] : [];
};

export const filterActions = (items: ActionListItem[], filter: ActionFilter) => items.filter(MATCHES[filter]);

export type DueGroup = 'overdue' | 'today' | 'week' | 'later' | 'none' | 'closed';

export const DUE_GROUP_LABELS: Record<DueGroup, string> = {
  overdue: 'En retard',
  today: 'Aujourd’hui',
  week: 'Cette semaine',
  later: 'Plus tard',
  none: 'Sans échéance',
  closed: 'Faites',
};

const DUE_GROUP_ORDER: DueGroup[] = ['overdue', 'today', 'week', 'later', 'none', 'closed'];

const dayStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const parseDue = (value: string): Date | null => {
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** The due-date group of one item; « cette semaine » ends on Sunday. */
export const dueGroup = (item: ActionListItem, now: Date = new Date()): DueGroup => {
  if (isClosed(item)) return 'closed';
  const due = item.due ? parseDue(item.due) : null;
  if (!due) return 'none';
  const today = dayStart(now);
  const day = dayStart(due);
  if (day < today) return 'overdue';
  if (day === today) return 'today';
  const daysToSunday = (7 - now.getDay()) % 7;
  return day <= today + daysToSunday * 86_400_000 ? 'week' : 'later';
};

/**
 * Items grouped by due date, when the list is long and most items carry a
 * valid date; null otherwise (the list then keeps its plain order).
 */
export const groupByDue = (items: ActionListItem[], all: ActionListItem[], now: Date = new Date()): Array<{ group: DueGroup; label: string; items: ActionListItem[] }> | null => {
  const open = all.filter((item) => !isClosed(item));
  const dated = open.filter((item) => item.due && parseDue(item.due));
  if (all.length < ACTION_LIST_INTERACTIVE_FROM || open.length === 0 || dated.length < open.length / 2) return null;
  const groups = new Map<DueGroup, ActionListItem[]>();
  for (const item of items) {
    const group = dueGroup(item, now);
    groups.set(group, [...(groups.get(group) ?? []), item]);
  }
  return DUE_GROUP_ORDER.filter((group) => groups.has(group))
    .map((group) => ({ group, label: DUE_GROUP_LABELS[group], items: groups.get(group)! }));
};
